export type Locale = 'zh' | 'en' | 'th'

type MessageValue = string | Record<string, MessageValue>

const LOCALE_STORAGE_KEY = 'app_locale_preference'
const DEFAULT_LOCALE: Locale = 'zh'

const messages: Record<Locale, Record<string, MessageValue>> = {
  zh: {
    common: {
      appName: '万悟AI学习平台',
      currentCompany: '当前公司',
      loading: '加载中',
      loadingEllipsis: '加载中...',
      loadingPleaseWait: '加载中，请稍后',
      fetchFailed: '获取失败',
      retryLater: '请稍后重试',
      notLoggedIn: '未登录',
      loginFirst: '请先登录',
      unnamedPractice: '未命名练习',
      gradeLabel: '评级：',
      completed: '已完成',
      ongoing: '进行中',
      notCompleted: '未完成',
      practice: '练习',
      assessment: '考核',
      noMore: '没有更多了',
      language: '语言',
      languageChanged: '语言已切换',
      chinese: '中文',
      english: 'English',
      thai: 'ไทย',
      back: '返回',
      close: '关闭',
      confirm: '确认',
      cancel: '取消',
      retry: '重试',
      submit: '提交',
      save: '保存',
      saving: '保存中...',
      add: '添加',
      edit: '编辑',
      done: '完成',
      testing: '检测中',
      companyLogo: '公司 logo',
      feedbackSubmitted: '提交成功',
      submitFailed: '提交失败',
      submitPending: '提交中...',
      submitPendingAlt: '提交中…',
      thankYouForFeedback: '感谢你的反馈',
      reminder: '温馨提醒',
      resumePracticeDetected: '检测到您有一场未完成的陪练',
      continuePractice: '请继续完成陪练',
      backToEvaluation: '回到评测',
      backHome: '返回首页',
      networkOrAdmin: '请检查网络连接或联系管理员',
      unknownError: '未知错误',
      relogin: '请重新登录',
      noRecord: '暂无记录',
      task: '任务',
      attachment: '附件',
      unsupportedPreview: '无法预览该附件'
    },
    app: {
      practiceDisabledTitle: '该公司未开通陪练业务',
      practiceDisabledMessage: '{company}暂未开通陪练功能，如需使用请联系管理员',
      loadingApp: '正在加载...',
      verifyingEligibility: '正在验证评测资格...',
      loadingCompanyInfo: '正在加载公司信息...',
      verifyingIdentity: '正在验证身份信息...',
      evaluationStatusError: '评测状态异常',
      verifyFailed: '验证失败',
      verifyFailedRetry: '验证失败，请稍后重试',
      jwtInvalid: 'JWT验证失败您的访问链接已失效或无权访问此页面'
    },
    user: {
      title: '个人中心',
      completedPractice: '完成练习',
      averageGrade: '平均评级',
      recentPractice: '最近练习',
      viewMore: '查看更多>',
      settings: '设置',
      changePassword: '修改密码',
      feedback: '意见反馈',
      logout: '退出登录',
      logoutConfirmTitle: '是否退出登录？',
      cancel: '取消',
      confirm: '确认',
      practiceLoadingAlt: '加载中',
      emptyPracticeAlt: '暂无练习记录',
      openFeedbackFailed: '无法打开反馈页',
      missingUserInfo: '未获取到用户信息',
      loadProfileFailed: '加载完成练习与平均评级失败，请稍后重试',
      loadRecentPracticeFailed: '获取最近练习失败，请稍后重试',
      pageTitle: '个人中心 - 万悟AI学习平台',
      pageDescription: '查看个人信息与最近练习'
    },
    tip: {
      title: '评测须知',
      duration: '预计用时30~45分钟',
      start: '我已了解，开始评测',
      missingInfo: '评测信息缺失',
      restartFromHome: '请重新从首页开始评测',
      notices: {
        voiceTitle: '语音交互',
        voiceDesc: 'AI面试官将通过语音和文字提问，请用语音回答',
        retryTitle: '重答机会',
        retryDesc: '每道题都可以重新回答，请放松心态如实回答',
        networkTitle: '网络通畅',
        networkDesc: '请确保网络连接稳定，避免中途断网影响评测',
        quietTitle: '安静环境',
        quietDesc: '请勿在嘈杂环境中进行评测，避免影响识别结果'
      }
    },
    prepare: {
      title: '设备检测',
      subtitle: '为确保练习，请允许以下权限',
      microphone: '麦克风权限',
      speaker: '扬声器',
      volumeTest: '音量测试',
      voiceTip: '全程将会使用语音交流，请在您舒适的环境下开始',
      next: '检测完成，下一步',
      playingAudio: '正在播放测试音频',
      adjustVolume: '请将手机调整到合适音量',
      microphoneRequired: '请允许麦克风权限',
      configMissing: '陪练配置缺失',
      reselectModule: '请重新选择陪练模块',
      userMissing: '用户信息缺失',
      initializing: '正在初始化陪练...'
    },
    chat: {
      preparing: '评审准备中',
      taskDetail: '任务详情',
      practiceRoom: '练习间',
      endPractice: '结束练习',
      questionProgress: '问题：{current}/{total}',
      hint: '提示',
      thinking: '对方正在思考中...',
      tapToSpeak: '点击说话',
      devicePreparing: '设备准备中...',
      preparingMic: '正在准备收音...',
      listening: '请说，我在聆听...',
      answerAgain: '重答',
      send: '发送',
      autoEndIn: '陪练将在 {seconds}s 后结束',
      reportComing: '即将为您提供陪练报告',
      endNow: '立即结束',
      cancelAnswerTitle: '重新回答',
      cancelAnswerMessage: '您确定要放弃当前回答，重新作答吗？',
      endConfirmTitle: '结束陪练',
      endConfirmMessage: '确定要结束当前陪练吗？结束后将生成陪练报告。',
      taskBackground: '任务背景',
      taskSubtitle: '请仔细阅读以下信息完成挑战',
      tutorialSpeak: '点击这里说话，无需长按',
      loadingHint: '正在加载提示...',
      defaultHintEmpty: '暂无提示内容',
      hintLoadFailed: '获取提示失败，请稍后再试',
      roleFallback: '角色',
      microphonePrepareFailed: '麦克风准备失败',
      asrFailed: '语音识别失败',
      asrRetryHint: '没有识别清楚，请再说一次。',
      asrNoResult: '没有识别到内容',
      asrNoResultRetryHint: '没有识别到内容，请再说一次。',
      asrUploadFailed: '网络异常，发送失败',
      asrUploadRetryHint: '语音发送失败，请检查网络后重试。',
      noSpeechDetected: '没有听到声音',
      noSpeechRetryHint: '没有听到清晰声音，请靠近麦克风再试一次。',
      endFailed: '结束陪练失败',
      ended: '陪练已成功结束',
      createFailed: '创建评审对话失败',
      notLoggedIn: '用户未登录',
      noOngoing: '没有正在进行的陪练记录',
      ttsFailed: '文本转换语音失败',
      resumeFailed: '恢复陪练失败',
      conversationFailed: '对话问答失败',
      serviceErrorContinue: '系统服务错误，请继续问其他问题'
    },
    end: {
      completed: '评测已完成',
      thanks: '感谢你的参与！',
      resultNotice: '评测结果将由公司HR同事反馈给你'
    },
    unauthorized: {
      title: '访问权限无效',
      errorCode: '错误代码: 401',
      contactAdmin: '如有疑问，请联系系统管理员',
      defaultMessage: '您的访问链接已失效或无权访问此页面',
      pageTitle: '访问权限无效 - 万悟AI学习平台'
    },
    login: {
      pageTitle: '中层管理后备人才腾飞计划',
      appTitle: '万悟AI学习平台',
      usernamePlaceholder: '请输入用户名',
      passwordPlaceholder: '请输入密码',
      login: '登录',
      loggingIn: '登录中...',
      missingCredentials: '请输入用户名和密码',
      companyLoadFailed: '公司信息获取失败',
      refreshRetry: '请刷新页面重试',
      loginSuccess: '登录成功',
      loginFailed: '登录失败',
      invalidCredentials: '用户名或密码错误',
      heroAlt: '登录主视觉',
      showPassword: '显示密码',
      hidePassword: '隐藏密码'
    },
    feedback: {
      title: '意见反馈',
      placeholder: '请输入您的意见或建议（1–2000 字）',
      submit: '提交意见'
    },
    password: {
      title: '修改密码',
      oldPassword: '输入原密码',
      newPassword: '输入新的密码',
      confirmPassword: '请再输入一次确认',
      passwordHint: '6位密码，至少包括英文字母、数字、特殊符号中的2种',
      hidePassword: '隐藏密码',
      showPassword: '显示密码',
      passwordTooShort: '密码长度不能少于6位',
      passwordWeak: '密码至少包括英文字母、数字、特殊符号中的2种',
      passwordMismatch: '两次输入的新密码不一致',
      changeSuccess: '密码修改成功',
      changeFailed: '修改密码失败，请重试'
    },
    module: {
      pageTitle: '模块详情 - 万悟AI学习平台',
      pageDescription: '模块详情与练习说明',
      missingModuleParam: '缺少模块参数',
      moduleNotFound: '未找到模块配置',
      sceneBackground: '场景背景',
      yourRole: '您的角色',
      defaultUserRole: '资深销售顾问',
      aiRole: 'AI 的角色',
      personalityTypes: '{count}种性格类型',
      target: '通关目标',
      goPractice: '去练习',
      startAssessment: '开始考核',
      sceneIntro: '场景介绍'
    },
    uploader: {
      uploadAttachment: '上传附件',
      supportedFormats: '支持PDF、Word、Excel 格式',
      maxFilesExceeded: '最多只能上传{count}个文件',
      fileCountExceeded: '文件数量超过限制',
      unsupportedType: '不支持的文件类型: {name}',
      fileTooLarge: '文件大小超过限制: {name}',
      fileExists: '文件已存在: {name}',
      uploadFailedRetry: '上传失败，请重试'
    },
    scenario: {
      locked: '待解锁',
      completedGrade: '已完成：{grade}'
    },
    map: {
      greeting: '你好，{name}',
      subtitle: '准备好签单了吗？',
      topicTraining: '专题训练',
      comingSoon: '暂未开放',
      unsupportedModule: '模块类型暂不支持',
      practiceCount: '练习 {count} 次',
      defaultScenario: '场景',
      loadHomeListFailed: '首页场景列表加载失败，请稍后重试',
      eligibilityFailed: '验证陪练资格失败，请稍后重试',
      pageTitle: '首页 - 万悟AI学习平台',
      pageDescription: '中层管理后备人才腾飞计划首页'
    },
    tabbar: {
      home: '首页',
      mine: '我的'
    },
    legacyUser: {
      title: '个人中心',
      exit: '退出',
      feedback: '问题反馈',
      interviewRecords: '面试记录',
      duration: '用时：',
      loadingMore: '加载中...',
      noMore: '没有更多了',
      statusWaiting: '等待面试',
      statusOngoing: '面试进行中',
      statusCompleted: '面试已完成',
      evalGenerating: '评价生成中',
      evalCompleted: '评价已完成',
      evalFailed: '评价生成失败',
      evalSecurityFailed: '内容违规'
    },
    practice: {
      title: '最近练习',
      loadingAlt: '加载中',
      emptyAlt: '暂无练习记录',
      loadListFailed: '获取练习记录列表失败，请稍后重试',
      pageTitle: '最近练习 - 万悟AI学习平台',
      pageDescription: '查看最近练习记录'
    },
    potential: {
      title: '潜能评测',
      pageTitle: '潜能评测报告',
      pageDescription: '查看您的潜能评测报告',
      loadingPdfViewer: '加载PDF查看器...',
      loadPdfFailed: '加载PDF失败',
      fetchReportFailed: '获取报告失败'
    },
    topic: {
      title: '专题详情',
      empty: '暂无场景',
      cardDetailEmpty: '暂无详情内容',
      cardDetailNotFound: '未找到对应卡片内容',
      loadCardDetailFailed: '加载专题卡片详情失败，请稍后重试',
      loadFailed: '专题场景列表加载失败，请稍后重试',
      pageTitle: '专题详情 - 万悟AI学习平台',
      pageDescription: '专题场景列表'
    },
    info: {
      step1Title: '填写岗位信息',
      step2Title: '确认目标岗位和公司',
      step2Tip: 'AI识别结果可能会犯错，请核查信息是否正确',
      step3Title: '选择简历',
      step3Tip: '<span class="text-[red]">*</span>隐私声明：建立信息将严格保密，仅用于当前面试评估',
      step4Title: '设备检测',
      next: '下一步',
      prev: '返回',
      enterInterview: '进入面试间',
      autoRecognizing: '自动识别中',
      positionPlaceholder: '请输入岗位名称',
      companyPlaceholder: '请输入公司名称',
      noInfoDetected: '未识别到对应信息，请手动输入',
      autoExtractFailed: '自动识别失败',
      positionTextareaPlaceholder: '请粘贴或填写岗位信息，包括：岗位名称、公司名称、职位描述(JD)...',
      chooseHistoryResume: '选择历史简历',
      loadHistoryResumeFailed: '获取历史简历失败',
      missingJobInfo: '请填写岗位信息',
      missingPositionAndCompany: '请填写岗位名称以及公司名称',
      missingPosition: '请填写岗位名称',
      missingCompany: '请填写公司名称',
      missingResume: '请上传简历或者选择历史简历',
      missingMic: '请开启麦克风'
    },
    result: {
      practiceResult: '练习结果',
      waitingTitle: '报告生成可能需要一些时间<br></br>请耐心等待',
      processingConversation: '正在处理对话内容...',
      analyzingCommunication: '正在分析沟通技巧...',
      evaluatingEmotion: '正在评估情绪管理...',
      generatingDetailed: '正在生成详细报告...',
      overallScore: '综合得分',
      practiceDuration: '练习时长：{minutes}分钟',
      tabsLabel: '报告视图',
      resultTab: '考核结果',
      reviewTab: '对话复盘',
      abilityEvaluation: '能力评估',
      detailedEvaluation: '详细评价',
      objectiveCompletion: '目标完成情况',
      doneWell: '做得好：',
      issues: '存在问题：',
      improve: '建议改进：',
      suggestions: '建议',
      reviewLoadFailed: '对话记录加载失败',
      reviewLoading: '加载中，请稍后',
      noConversation: '暂无对话记录',
      missingThread: '缺少 thread_id，无法加载对话',
      loadFailed: '加载失败',
      roundContent: '第{round}轮：{content}',
      pageTitle: '考核结果 - 万悟AI学习平台',
      pageDescription: '陪练考核结果与能力评估'
    },
    resultComplete: {
      generatingReport: '正在生成评估报告',
      interviewerGrading: '面试官正在打分儿...',
      interviewerEvaluation: '面试官正在为你撰写评价...',
      interviewerGuidance: '面试官正在为你撰写指导意见...',
      securityBlocked: '内容违规，暂时无法显示',
      interviewReport: '面试评估报告',
      abilityTab: '能力评估',
      jobTab: '岗位匹配',
      answerTab: '回答分析',
      adviceTab: '建议',
      comprehensiveReport: '综合评价报告',
      abilityTable: '能力评估表',
      abilityDetail: '具体能力表现',
      jobAnalysis: '岗位匹配分析',
      answerAnalysis: '问题回答分析',
      generateFailed: '生成结果失败'
    }
  },
  en: {
    common: {
      appName: 'Wangu AI Learning Platform',
      currentCompany: 'Current company',
      loading: 'Loading',
      loadingEllipsis: 'Loading...',
      loadingPleaseWait: 'Loading, please wait',
      fetchFailed: 'Request failed',
      retryLater: 'Please try again later',
      notLoggedIn: 'Not logged in',
      loginFirst: 'Please log in first',
      unnamedPractice: 'Untitled practice',
      gradeLabel: 'Grade: ',
      completed: 'Completed',
      ongoing: 'In progress',
      notCompleted: 'Not completed',
      practice: 'Practice',
      assessment: 'Assessment',
      noMore: 'No more records',
      language: 'Language',
      languageChanged: 'Language updated',
      chinese: '中文',
      english: 'English',
      thai: 'ไทย',
      back: 'Back',
      close: 'Close',
      confirm: 'Confirm',
      cancel: 'Cancel',
      retry: 'Retry',
      submit: 'Submit',
      save: 'Save',
      saving: 'Saving...',
      add: 'Add',
      edit: 'Edit',
      done: 'Done',
      testing: 'Testing',
      companyLogo: 'Company logo',
      feedbackSubmitted: 'Submitted successfully',
      submitFailed: 'Submission failed',
      submitPending: 'Submitting...',
      submitPendingAlt: 'Submitting…',
      thankYouForFeedback: 'Thank you for your feedback',
      reminder: 'Reminder',
      resumePracticeDetected: 'An unfinished practice session was detected',
      continuePractice: 'Please continue the practice session',
      backToEvaluation: 'Back to evaluation',
      backHome: 'Back to home',
      networkOrAdmin: 'Please check your network connection or contact the administrator',
      unknownError: 'Unknown error',
      relogin: 'Please log in again',
      noRecord: 'No records',
      task: 'Task',
      attachment: 'Attachment',
      unsupportedPreview: 'Unable to preview this attachment'
    },
    app: {
      practiceDisabledTitle: 'Practice service is not enabled for this company',
      practiceDisabledMessage: '{company} has not enabled practice yet. Please contact the administrator if needed.',
      loadingApp: 'Loading...',
      verifyingEligibility: 'Verifying evaluation eligibility...',
      loadingCompanyInfo: 'Loading company information...',
      verifyingIdentity: 'Verifying identity...',
      evaluationStatusError: 'Unexpected evaluation status',
      verifyFailed: 'Verification failed',
      verifyFailedRetry: 'Verification failed. Please try again later.',
      jwtInvalid: 'JWT verification failed. Your access link is invalid or you do not have permission to access this page.'
    },
    user: {
      title: 'Profile',
      completedPractice: 'Completed practice',
      averageGrade: 'Average grade',
      recentPractice: 'Recent practice',
      viewMore: 'View more >',
      settings: 'Settings',
      changePassword: 'Change password',
      feedback: 'Feedback',
      logout: 'Log out',
      logoutConfirmTitle: 'Log out?',
      cancel: 'Cancel',
      confirm: 'Confirm',
      practiceLoadingAlt: 'Loading',
      emptyPracticeAlt: 'No practice records',
      openFeedbackFailed: 'Unable to open feedback page',
      missingUserInfo: 'User information is unavailable',
      loadProfileFailed: 'Failed to load completed practice and average grade. Please try again later.',
      loadRecentPracticeFailed: 'Failed to load recent practice. Please try again later.',
      pageTitle: 'Profile - Wangu AI Learning Platform',
      pageDescription: 'View profile information and recent practice'
    },
    tip: {
      title: 'Evaluation notes',
      duration: 'Estimated time: 30-45 minutes',
      start: 'Got it, start evaluation',
      missingInfo: 'Evaluation information is missing',
      restartFromHome: 'Please restart the evaluation from the home page',
      notices: {
        voiceTitle: 'Voice interaction',
        voiceDesc: 'The AI interviewer will ask questions by voice and text. Please answer by voice.',
        retryTitle: 'Second chances',
        retryDesc: 'You can answer each question again. Relax and answer honestly.',
        networkTitle: 'Stable network',
        networkDesc: 'Please make sure your network is stable to avoid interruptions during the evaluation.',
        quietTitle: 'Quiet environment',
        quietDesc: 'Please avoid noisy environments to prevent recognition issues.'
      }
    },
    prepare: {
      title: 'Device check',
      subtitle: 'To ensure a smooth practice session, please allow the following permissions',
      microphone: 'Microphone',
      speaker: 'Speaker',
      volumeTest: 'Volume test',
      voiceTip: 'Voice communication will be used throughout. Please start in a comfortable environment.',
      next: 'Checks complete, next step',
      playingAudio: 'Playing test audio',
      adjustVolume: 'Please adjust your phone to a suitable volume',
      microphoneRequired: 'Please allow microphone access',
      configMissing: 'Practice configuration is missing',
      reselectModule: 'Please select the practice module again',
      userMissing: 'User information is missing',
      initializing: 'Initializing practice...'
    },
    chat: {
      preparing: 'Preparing review',
      taskDetail: 'Task details',
      practiceRoom: 'Practice room',
      endPractice: 'End practice',
      questionProgress: 'Question: {current}/{total}',
      hint: 'Tips',
      thinking: 'Thinking...',
      tapToSpeak: 'Tap to speak',
      devicePreparing: 'Preparing device...',
      preparingMic: 'Preparing microphone...',
      listening: 'Please speak, I am listening...',
      answerAgain: 'Answer again',
      send: 'Send',
      autoEndIn: 'Practice will end in {seconds}s',
      reportComing: 'Your practice report will be ready shortly',
      endNow: 'End now',
      cancelAnswerTitle: 'Answer again',
      cancelAnswerMessage: 'Are you sure you want to discard the current answer and answer again?',
      endConfirmTitle: 'End practice',
      endConfirmMessage: 'Are you sure you want to end the current practice session? A report will be generated afterward.',
      taskBackground: 'Task background',
      taskSubtitle: 'Please read the following information carefully to complete the challenge',
      tutorialSpeak: 'Tap here to speak — no long press needed',
      loadingHint: 'Loading tips...',
      defaultHintEmpty: 'No tips available',
      hintLoadFailed: 'Failed to load tips. Please try again later.',
      roleFallback: 'Role',
      microphonePrepareFailed: 'Failed to prepare the microphone',
      asrFailed: 'Speech recognition failed',
      asrRetryHint: 'I could not catch that. Please try again.',
      asrNoResult: 'No content recognized',
      asrNoResultRetryHint: 'No content was recognized. Please try again.',
      asrUploadFailed: 'Network issue: send failed',
      asrUploadRetryHint: 'Your voice could not be sent. Check your network and try again.',
      noSpeechDetected: 'No speech detected',
      noSpeechRetryHint: 'I did not hear clear speech. Move closer to the microphone and try again.',
      endFailed: 'Failed to end practice',
      ended: 'Practice ended successfully',
      createFailed: 'Failed to create the review conversation',
      notLoggedIn: 'User not logged in',
      noOngoing: 'No ongoing practice session found',
      ttsFailed: 'Text-to-speech failed',
      resumeFailed: 'Failed to resume practice',
      conversationFailed: 'Conversation failed',
      serviceErrorContinue: 'The service encountered an error. Please continue with another question.'
    },
    end: {
      completed: 'Evaluation completed',
      thanks: 'Thank you for participating!',
      resultNotice: 'Your evaluation result will be shared with you by your company HR team.'
    },
    unauthorized: {
      title: 'Access denied',
      errorCode: 'Error code: 401',
      contactAdmin: 'If you have questions, please contact the system administrator',
      defaultMessage: 'Your access link has expired or you do not have permission to access this page.',
      pageTitle: 'Access Denied - Wangu AI Learning Platform'
    },
    login: {
      pageTitle: 'Mid-Level Management Talent Advancement Program',
      appTitle: 'Wangu AI Learning Platform',
      usernamePlaceholder: 'Enter username',
      passwordPlaceholder: 'Enter password',
      login: 'Log in',
      loggingIn: 'Logging in...',
      missingCredentials: 'Please enter your username and password',
      companyLoadFailed: 'Failed to load company information',
      refreshRetry: 'Please refresh the page and try again',
      loginSuccess: 'Login successful',
      loginFailed: 'Login failed',
      invalidCredentials: 'Incorrect username or password',
      heroAlt: 'Login hero image',
      showPassword: 'Show password',
      hidePassword: 'Hide password'
    },
    feedback: {
      title: 'Feedback',
      placeholder: 'Please enter your feedback or suggestion (1-2000 characters)',
      submit: 'Submit feedback'
    },
    password: {
      title: 'Change password',
      oldPassword: 'Enter current password',
      newPassword: 'Enter new password',
      confirmPassword: 'Enter again to confirm',
      passwordHint: 'At least 6 characters and includes at least two of letters, numbers, or special characters',
      hidePassword: 'Hide password',
      showPassword: 'Show password',
      passwordTooShort: 'Password must be at least 6 characters long',
      passwordWeak: 'Password must include at least two of letters, numbers, or special characters',
      passwordMismatch: 'The two new passwords do not match',
      changeSuccess: 'Password changed successfully',
      changeFailed: 'Failed to change password. Please try again.'
    },
    module: {
      pageTitle: 'Module Details - Wangu AI Learning Platform',
      pageDescription: 'Module details and practice instructions',
      missingModuleParam: 'Missing module parameter',
      moduleNotFound: 'Module configuration not found',
      sceneBackground: 'Scenario background',
      yourRole: 'Your role',
      defaultUserRole: 'Senior sales consultant',
      aiRole: 'AI role',
      personalityTypes: '{count} personality types',
      target: 'Target',
      goPractice: 'Go practice',
      startAssessment: 'Start assessment',
      sceneIntro: 'Scenario introduction'
    },
    uploader: {
      uploadAttachment: 'Upload attachment',
      supportedFormats: 'Supports PDF, Word, and Excel formats',
      maxFilesExceeded: 'You can upload up to {count} files',
      fileCountExceeded: 'File count exceeds the limit',
      unsupportedType: 'Unsupported file type: {name}',
      fileTooLarge: 'File size exceeds the limit: {name}',
      fileExists: 'File already exists: {name}',
      uploadFailedRetry: 'Upload failed. Please try again.'
    },
    scenario: {
      locked: 'Locked',
      completedGrade: 'Completed: {grade}'
    },
    map: {
      greeting: 'Hello, {name}',
      subtitle: 'Ready to close the deal?',
      topicTraining: 'Topic training',
      comingSoon: 'Not available yet',
      unsupportedModule: 'This module type is not supported yet',
      practiceCount: 'Practiced {count} times',
      defaultScenario: 'Scenario',
      loadHomeListFailed: 'Failed to load the home scenario list. Please try again later.',
      eligibilityFailed: 'Failed to verify practice eligibility. Please try again later.',
      pageTitle: 'Home - Wangu AI Learning Platform',
      pageDescription: 'Home page for the Mid-Level Management Talent Advancement Program'
    },
    tabbar: {
      home: 'Home',
      mine: 'Profile'
    },
    legacyUser: {
      title: 'Profile',
      exit: 'Exit',
      feedback: 'Feedback',
      interviewRecords: 'Interview records',
      duration: 'Duration: ',
      loadingMore: 'Loading...',
      noMore: 'No more records',
      statusWaiting: 'Waiting for interview',
      statusOngoing: 'Interview in progress',
      statusCompleted: 'Interview completed',
      evalGenerating: 'Generating evaluation',
      evalCompleted: 'Evaluation completed',
      evalFailed: 'Evaluation failed',
      evalSecurityFailed: 'Content policy violation'
    },
    practice: {
      title: 'Recent practice',
      loadingAlt: 'Loading',
      emptyAlt: 'No practice records',
      loadListFailed: 'Failed to load practice records. Please try again later.',
      pageTitle: 'Recent Practice - Wangu AI Learning Platform',
      pageDescription: 'View recent practice records'
    },
    potential: {
      title: 'Potential assessment',
      pageTitle: 'Potential assessment report',
      pageDescription: 'View your potential assessment report',
      loadingPdfViewer: 'Loading PDF viewer...',
      loadPdfFailed: 'Failed to load PDF',
      fetchReportFailed: 'Failed to fetch report'
    },
    topic: {
      title: 'Topic details',
      empty: 'No scenarios',
      cardDetailEmpty: 'No detail content available.',
      cardDetailNotFound: 'The corresponding card content was not found.',
      loadCardDetailFailed: 'Failed to load topic card details. Please try again later.',
      loadFailed: 'Failed to load topic scenarios. Please try again later.',
      pageTitle: 'Topic Details - Wangu AI Learning Platform',
      pageDescription: 'Topic scenario list'
    },
    info: {
      step1Title: 'Fill in position information',
      step2Title: 'Confirm target position and company',
      step2Tip: 'AI recognition may make mistakes. Please verify whether the information is correct.',
      step3Title: 'Select resume',
      step3Tip: '<span class="text-[red]">*</span>Privacy statement: Your information will be kept strictly confidential and used only for the current interview assessment.',
      step4Title: 'Device check',
      next: 'Next',
      prev: 'Back',
      enterInterview: 'Enter interview room',
      autoRecognizing: 'Recognizing automatically',
      positionPlaceholder: 'Enter position name',
      companyPlaceholder: 'Enter company name',
      noInfoDetected: 'The information was not recognized. Please enter it manually.',
      autoExtractFailed: 'Automatic recognition failed',
      positionTextareaPlaceholder: 'Please paste or enter the position information, including: position name, company name, and job description (JD)...',
      chooseHistoryResume: 'Select historical resume',
      loadHistoryResumeFailed: 'Failed to load historical resumes',
      missingJobInfo: 'Please fill in the position information',
      missingPositionAndCompany: 'Please fill in both the position name and company name',
      missingPosition: 'Please fill in the position name',
      missingCompany: 'Please fill in the company name',
      missingResume: 'Please upload a resume or select a historical resume',
      missingMic: 'Please enable the microphone'
    },
    result: {
      practiceResult: 'Practice result',
      waitingTitle: 'Report generation may take some time<br></br>Please be patient',
      processingConversation: 'Processing the conversation...',
      analyzingCommunication: 'Analyzing communication skills...',
      evaluatingEmotion: 'Assessing emotional management...',
      generatingDetailed: 'Generating detailed report...',
      overallScore: 'Overall score',
      practiceDuration: 'Practice duration: {minutes} min',
      tabsLabel: 'Report views',
      resultTab: 'Assessment result',
      reviewTab: 'Conversation review',
      abilityEvaluation: 'Ability evaluation',
      detailedEvaluation: 'Detailed evaluation',
      objectiveCompletion: 'Goal completion',
      doneWell: 'Done well:',
      issues: 'Issues:',
      improve: 'Suggested improvements:',
      suggestions: 'Suggestions',
      reviewLoadFailed: 'Failed to load conversation records',
      reviewLoading: 'Loading, please wait',
      noConversation: 'No conversation records',
      missingThread: 'Missing thread_id. Unable to load the conversation.',
      loadFailed: 'Load failed',
      roundContent: 'Round {round}: {content}',
      pageTitle: 'Assessment Result - Wangu AI Learning Platform',
      pageDescription: 'Practice assessment results and capability evaluation'
    },
    resultComplete: {
      generatingReport: 'Generating evaluation report',
      interviewerGrading: 'The interviewer is grading...',
      interviewerEvaluation: 'The interviewer is writing your evaluation...',
      interviewerGuidance: 'The interviewer is writing guidance for you...',
      securityBlocked: 'The content violates policy and cannot be displayed right now.',
      interviewReport: 'Interview evaluation report',
      abilityTab: 'Ability',
      jobTab: 'Job fit',
      answerTab: 'Answer analysis',
      adviceTab: 'Suggestions',
      comprehensiveReport: 'Comprehensive evaluation report',
      abilityTable: 'Ability evaluation table',
      abilityDetail: 'Detailed ability performance',
      jobAnalysis: 'Job match analysis',
      answerAnalysis: 'Question answer analysis',
      generateFailed: 'Failed to generate results'
    }
  },
  th: {
    common: {
      appName: 'แพลตฟอร์มการเรียนรู้ AI Wangu',
      currentCompany: 'บริษัทปัจจุบัน',
      loading: 'กำลังโหลด',
      loadingEllipsis: 'กำลังโหลด...',
      loadingPleaseWait: 'กำลังโหลด โปรดรอสักครู่',
      fetchFailed: 'ดึงข้อมูลไม่สำเร็จ',
      retryLater: 'โปรดลองอีกครั้งภายหลัง',
      notLoggedIn: 'ยังไม่ได้เข้าสู่ระบบ',
      loginFirst: 'กรุณาเข้าสู่ระบบก่อน',
      unnamedPractice: 'แบบฝึกที่ไม่มีชื่อ',
      gradeLabel: 'ระดับ: ',
      completed: 'เสร็จสิ้นแล้ว',
      ongoing: 'กำลังดำเนินการ',
      notCompleted: 'ยังไม่เสร็จสิ้น',
      practice: 'ฝึกซ้อม',
      assessment: 'ประเมิน',
      noMore: 'ไม่มีข้อมูลเพิ่มเติม',
      language: 'ภาษา',
      languageChanged: 'เปลี่ยนภาษาแล้ว',
      chinese: '中文',
      english: 'English',
      thai: 'ไทย',
      back: 'กลับ',
      close: 'ปิด',
      confirm: 'ยืนยัน',
      cancel: 'ยกเลิก',
      retry: 'ลองอีกครั้ง',
      submit: 'ส่ง',
      save: 'บันทึก',
      saving: 'กำลังบันทึก...',
      add: 'เพิ่ม',
      edit: 'แก้ไข',
      done: 'เสร็จสิ้น',
      testing: 'กำลังทดสอบ',
      companyLogo: 'โลโก้บริษัท',
      feedbackSubmitted: 'ส่งสำเร็จ',
      submitFailed: 'ส่งไม่สำเร็จ',
      submitPending: 'กำลังส่ง...',
      submitPendingAlt: 'กำลังส่ง…',
      thankYouForFeedback: 'ขอบคุณสำหรับความคิดเห็น',
      reminder: 'ข้อควรทราบ',
      resumePracticeDetected: 'ตรวจพบว่าคุณมีการฝึกซ้อมที่ยังไม่เสร็จ',
      continuePractice: 'กรุณาฝึกซ้อมต่อให้เสร็จ',
      backToEvaluation: 'กลับไปที่การประเมิน',
      backHome: 'กลับหน้าหลัก',
      networkOrAdmin: 'กรุณาตรวจสอบเครือข่ายหรือติดต่อผู้ดูแลระบบ',
      unknownError: 'ข้อผิดพลาดที่ไม่ทราบสาเหตุ',
      relogin: 'กรุณาเข้าสู่ระบบใหม่',
      noRecord: 'ยังไม่มีข้อมูล',
      task: 'ภารกิจ',
      attachment: 'ไฟล์แนบ',
      unsupportedPreview: 'ไม่สามารถแสดงตัวอย่างไฟล์แนบนี้ได้'
    },
    app: {
      practiceDisabledTitle: 'บริษัทยังไม่เปิดใช้งานบริการฝึกซ้อม',
      practiceDisabledMessage: '{company} ยังไม่ได้เปิดใช้งานฟังก์ชันฝึกซ้อม หากต้องการใช้งานกรุณาติดต่อผู้ดูแลระบบ',
      loadingApp: 'กำลังโหลด...',
      verifyingEligibility: 'กำลังตรวจสอบสิทธิ์การประเมิน...',
      loadingCompanyInfo: 'กำลังโหลดข้อมูลบริษัท...',
      verifyingIdentity: 'กำลังตรวจสอบตัวตน...',
      evaluationStatusError: 'สถานะการประเมินผิดปกติ',
      verifyFailed: 'การตรวจสอบล้มเหลว',
      verifyFailedRetry: 'การตรวจสอบล้มเหลว โปรดลองอีกครั้งภายหลัง',
      jwtInvalid: 'การตรวจสอบ JWT ล้มเหลว ลิงก์การเข้าถึงของคุณไม่ถูกต้องหรือคุณไม่มีสิทธิ์เข้าถึงหน้านี้'
    },
    user: {
      title: 'โปรไฟล์',
      completedPractice: 'แบบฝึกที่เสร็จสิ้น',
      averageGrade: 'ระดับเฉลี่ย',
      recentPractice: 'การฝึกล่าสุด',
      viewMore: 'ดูเพิ่มเติม >',
      settings: 'การตั้งค่า',
      changePassword: 'เปลี่ยนรหัสผ่าน',
      feedback: 'ข้อเสนอแนะ',
      logout: 'ออกจากระบบ',
      logoutConfirmTitle: 'ออกจากระบบหรือไม่?',
      cancel: 'ยกเลิก',
      confirm: 'ยืนยัน',
      practiceLoadingAlt: 'กำลังโหลด',
      emptyPracticeAlt: 'ยังไม่มีประวัติการฝึก',
      openFeedbackFailed: 'ไม่สามารถเปิดหน้าข้อเสนอแนะได้',
      missingUserInfo: 'ไม่พบข้อมูลผู้ใช้',
      loadProfileFailed: 'โหลดจำนวนแบบฝึกที่เสร็จสิ้นและระดับเฉลี่ยไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      loadRecentPracticeFailed: 'โหลดการฝึกล่าสุดไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      pageTitle: 'โปรไฟล์ - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'ดูข้อมูลส่วนตัวและการฝึกล่าสุด'
    },
    tip: {
      title: 'คำแนะนำก่อนประเมิน',
      duration: 'ใช้เวลาประมาณ 30-45 นาที',
      start: 'เข้าใจแล้ว เริ่มการประเมิน',
      missingInfo: 'ข้อมูลการประเมินไม่ครบถ้วน',
      restartFromHome: 'กรุณาเริ่มการประเมินใหม่จากหน้าแรก',
      notices: {
        voiceTitle: 'โต้ตอบด้วยเสียง',
        voiceDesc: 'ผู้สัมภาษณ์ AI จะถามผ่านเสียงและข้อความ กรุณาตอบด้วยเสียง',
        retryTitle: 'โอกาสตอบใหม่',
        retryDesc: 'คุณสามารถตอบใหม่ได้ทุกคำถาม กรุณาผ่อนคลายและตอบตามจริง',
        networkTitle: 'เครือข่ายเสถียร',
        networkDesc: 'กรุณาตรวจสอบให้เครือข่ายเสถียรเพื่อหลีกเลี่ยงการหลุดระหว่างการประเมิน',
        quietTitle: 'สภาพแวดล้อมเงียบ',
        quietDesc: 'กรุณาอย่าทำการประเมินในที่เสียงดังเพื่อหลีกเลี่ยงผลต่อการรู้จำ'
      }
    },
    prepare: {
      title: 'ตรวจสอบอุปกรณ์',
      subtitle: 'เพื่อให้การฝึกซ้อมเป็นไปอย่างราบรื่น โปรดอนุญาตสิทธิ์ต่อไปนี้',
      microphone: 'ไมโครโฟน',
      speaker: 'ลำโพง',
      volumeTest: 'ทดสอบเสียง',
      voiceTip: 'ระบบจะใช้การสื่อสารด้วยเสียงตลอดการฝึก กรุณาเริ่มในสภาพแวดล้อมที่คุณสบายใจ',
      next: 'ตรวจสอบเสร็จแล้ว ขั้นตอนถัดไป',
      playingAudio: 'กำลังเล่นเสียงทดสอบ',
      adjustVolume: 'กรุณาปรับระดับเสียงโทรศัพท์ให้เหมาะสม',
      microphoneRequired: 'กรุณาอนุญาตการเข้าถึงไมโครโฟน',
      configMissing: 'ไม่มีการตั้งค่าการฝึกซ้อม',
      reselectModule: 'กรุณาเลือกโมดูลฝึกซ้อมอีกครั้ง',
      userMissing: 'ไม่พบข้อมูลผู้ใช้',
      initializing: 'กำลังเริ่มต้นการฝึกซ้อม...'
    },
    chat: {
      preparing: 'กำลังเตรียมการประเมิน',
      taskDetail: 'รายละเอียดภารกิจ',
      practiceRoom: 'ห้องฝึก',
      endPractice: 'จบการฝึก',
      questionProgress: 'คำถาม: {current}/{total}',
      hint: 'คำแนะนำ',
      thinking: 'อีกฝ่ายกำลังคิด...',
      tapToSpeak: 'แตะเพื่อพูด',
      devicePreparing: 'กำลังเตรียมอุปกรณ์...',
      preparingMic: 'กำลังเตรียมไมโครโฟน...',
      listening: 'พูดได้เลย ฉันกำลังฟังอยู่...',
      answerAgain: 'ตอบใหม่',
      send: 'ส่ง',
      autoEndIn: 'การฝึกจะจบในอีก {seconds} วินาที',
      reportComing: 'กำลังเตรียมรายงานการฝึกให้คุณ',
      endNow: 'จบทันที',
      cancelAnswerTitle: 'ตอบใหม่',
      cancelAnswerMessage: 'คุณแน่ใจหรือไม่ว่าต้องการยกเลิกคำตอบปัจจุบันและตอบใหม่?',
      endConfirmTitle: 'จบการฝึก',
      endConfirmMessage: 'คุณแน่ใจหรือไม่ว่าต้องการจบการฝึกนี้? ระบบจะสร้างรายงานหลังจากนั้น',
      taskBackground: 'พื้นหลังภารกิจ',
      taskSubtitle: 'โปรดอ่านข้อมูลต่อไปนี้อย่างละเอียดเพื่อทำภารกิจให้สำเร็จ',
      tutorialSpeak: 'แตะที่นี่เพื่อพูด ไม่ต้องกดค้าง',
      loadingHint: 'กำลังโหลดคำแนะนำ...',
      defaultHintEmpty: 'ยังไม่มีคำแนะนำ',
      hintLoadFailed: 'โหลดคำแนะนำไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      roleFallback: 'บทบาท',
      microphonePrepareFailed: 'เตรียมไมโครโฟนไม่สำเร็จ',
      asrFailed: 'การรู้จำเสียงล้มเหลว',
      asrRetryHint: 'ระบบฟังไม่ชัด กรุณาพูดอีกครั้ง',
      asrNoResult: 'ไม่พบเนื้อหาเสียง',
      asrNoResultRetryHint: 'ไม่พบเนื้อหาเสียง กรุณาพูดอีกครั้ง',
      asrUploadFailed: 'เครือข่ายขัดข้อง ส่งไม่สำเร็จ',
      asrUploadRetryHint: 'ส่งเสียงไม่สำเร็จ กรุณาตรวจสอบเครือข่ายแล้วลองอีกครั้ง',
      noSpeechDetected: 'ไม่ได้ยินเสียงพูด',
      noSpeechRetryHint: 'ไม่ได้ยินเสียงชัดเจน กรุณาเข้าใกล้ไมโครโฟนแล้วลองอีกครั้ง',
      endFailed: 'จบการฝึกไม่สำเร็จ',
      ended: 'จบการฝึกสำเร็จแล้ว',
      createFailed: 'สร้างบทสนทนาการประเมินไม่สำเร็จ',
      notLoggedIn: 'ผู้ใช้ยังไม่ได้เข้าสู่ระบบ',
      noOngoing: 'ไม่พบการฝึกที่กำลังดำเนินอยู่',
      ttsFailed: 'แปลงข้อความเป็นเสียงไม่สำเร็จ',
      resumeFailed: 'กลับเข้าสู่การฝึกไม่สำเร็จ',
      conversationFailed: 'การสนทนาล้มเหลว',
      serviceErrorContinue: 'ระบบเกิดข้อผิดพลาด กรุณาถามคำถามอื่นต่อได้'
    },
    end: {
      completed: 'การประเมินเสร็จสิ้นแล้ว',
      thanks: 'ขอบคุณที่เข้าร่วม!',
      resultNotice: 'ผลการประเมินจะถูกแจ้งให้คุณทราบโดยทีม HR ของบริษัท'
    },
    unauthorized: {
      title: 'ไม่มีสิทธิ์เข้าถึง',
      errorCode: 'รหัสข้อผิดพลาด: 401',
      contactAdmin: 'หากมีข้อสงสัย กรุณาติดต่อผู้ดูแลระบบ',
      defaultMessage: 'ลิงก์การเข้าถึงของคุณหมดอายุหรือคุณไม่มีสิทธิ์เข้าถึงหน้านี้',
      pageTitle: 'ไม่มีสิทธิ์เข้าถึง - แพลตฟอร์มการเรียนรู้ AI Wangu'
    },
    login: {
      pageTitle: 'โครงการเติบโตสำหรับบุคลากรสำรองระดับผู้จัดการ',
      appTitle: 'แพลตฟอร์มการเรียนรู้ AI Wangu',
      usernamePlaceholder: 'กรอกชื่อผู้ใช้',
      passwordPlaceholder: 'กรอกรหัสผ่าน',
      login: 'เข้าสู่ระบบ',
      loggingIn: 'กำลังเข้าสู่ระบบ...',
      missingCredentials: 'กรุณากรอกชื่อผู้ใช้และรหัสผ่าน',
      companyLoadFailed: 'โหลดข้อมูลบริษัทไม่สำเร็จ',
      refreshRetry: 'กรุณารีเฟรชหน้าและลองอีกครั้ง',
      loginSuccess: 'เข้าสู่ระบบสำเร็จ',
      loginFailed: 'เข้าสู่ระบบไม่สำเร็จ',
      invalidCredentials: 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง',
      heroAlt: 'ภาพหลักหน้าเข้าสู่ระบบ',
      showPassword: 'แสดงรหัสผ่าน',
      hidePassword: 'ซ่อนรหัสผ่าน'
    },
    feedback: {
      title: 'ข้อเสนอแนะ',
      placeholder: 'กรุณากรอกความคิดเห็นหรือข้อเสนอแนะของคุณ (1-2000 ตัวอักษร)',
      submit: 'ส่งข้อเสนอแนะ'
    },
    password: {
      title: 'เปลี่ยนรหัสผ่าน',
      oldPassword: 'กรอกรหัสผ่านเดิม',
      newPassword: 'กรอกรหัสผ่านใหม่',
      confirmPassword: 'กรอกอีกครั้งเพื่อยืนยัน',
      passwordHint: 'รหัสผ่านอย่างน้อย 6 ตัว และต้องมีอย่างน้อย 2 ประเภทจากตัวอักษร ตัวเลข หรืออักขระพิเศษ',
      hidePassword: 'ซ่อนรหัสผ่าน',
      showPassword: 'แสดงรหัสผ่าน',
      passwordTooShort: 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร',
      passwordWeak: 'รหัสผ่านต้องมีอย่างน้อย 2 ประเภทจากตัวอักษร ตัวเลข หรืออักขระพิเศษ',
      passwordMismatch: 'รหัสผ่านใหม่ทั้งสองครั้งไม่ตรงกัน',
      changeSuccess: 'เปลี่ยนรหัสผ่านสำเร็จ',
      changeFailed: 'เปลี่ยนรหัสผ่านไม่สำเร็จ โปรดลองอีกครั้ง'
    },
    module: {
      pageTitle: 'รายละเอียดโมดูล - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'รายละเอียดโมดูลและคำอธิบายการฝึก',
      missingModuleParam: 'ไม่มีพารามิเตอร์โมดูล',
      moduleNotFound: 'ไม่พบการตั้งค่าโมดูล',
      sceneBackground: 'พื้นหลังสถานการณ์',
      yourRole: 'บทบาทของคุณ',
      defaultUserRole: 'ที่ปรึกษาการขายอาวุโส',
      aiRole: 'บทบาทของ AI',
      personalityTypes: '{count} ประเภทบุคลิก',
      target: 'เป้าหมาย',
      goPractice: 'ไปฝึก',
      startAssessment: 'เริ่มการประเมิน',
      sceneIntro: 'แนะนำสถานการณ์'
    },
    uploader: {
      uploadAttachment: 'อัปโหลดไฟล์แนบ',
      supportedFormats: 'รองรับไฟล์ PDF, Word และ Excel',
      maxFilesExceeded: 'อัปโหลดได้สูงสุด {count} ไฟล์',
      fileCountExceeded: 'จำนวนไฟล์เกินที่กำหนด',
      unsupportedType: 'ไม่รองรับประเภทไฟล์: {name}',
      fileTooLarge: 'ขนาดไฟล์เกินกำหนด: {name}',
      fileExists: 'มีไฟล์นี้อยู่แล้ว: {name}',
      uploadFailedRetry: 'อัปโหลดไม่สำเร็จ โปรดลองอีกครั้ง'
    },
    scenario: {
      locked: 'ยังไม่ปลดล็อก',
      completedGrade: 'เสร็จแล้ว: {grade}'
    },
    map: {
      greeting: 'สวัสดี {name}',
      subtitle: 'พร้อมปิดการขายแล้วหรือยัง?',
      topicTraining: 'การฝึกเฉพาะหัวข้อ',
      comingSoon: 'ยังไม่เปิดให้ใช้งาน',
      unsupportedModule: 'ยังไม่รองรับประเภทโมดูลนี้',
      practiceCount: 'ฝึกแล้ว {count} ครั้ง',
      defaultScenario: 'สถานการณ์',
      loadHomeListFailed: 'โหลดรายการสถานการณ์หน้าแรกไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      eligibilityFailed: 'ตรวจสอบสิทธิ์การฝึกไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      pageTitle: 'หน้าแรก - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'หน้าแรกของโครงการเติบโตสำหรับบุคลากรสำรองระดับผู้จัดการ'
    },
    tabbar: {
      home: 'หน้าแรก',
      mine: 'โปรไฟล์'
    },
    legacyUser: {
      title: 'โปรไฟล์',
      exit: 'ออก',
      feedback: 'รายงานปัญหา',
      interviewRecords: 'ประวัติการสัมภาษณ์',
      duration: 'ใช้เวลา: ',
      loadingMore: 'กำลังโหลด...',
      noMore: 'ไม่มีข้อมูลเพิ่มเติม',
      statusWaiting: 'รอสัมภาษณ์',
      statusOngoing: 'กำลังสัมภาษณ์',
      statusCompleted: 'สัมภาษณ์เสร็จสิ้น',
      evalGenerating: 'กำลังสร้างการประเมิน',
      evalCompleted: 'ประเมินเสร็จสิ้น',
      evalFailed: 'สร้างการประเมินไม่สำเร็จ',
      evalSecurityFailed: 'เนื้อหาละเมิดนโยบาย'
    },
    practice: {
      title: 'การฝึกล่าสุด',
      loadingAlt: 'กำลังโหลด',
      emptyAlt: 'ยังไม่มีประวัติการฝึก',
      loadListFailed: 'โหลดรายการแบบฝึกไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      pageTitle: 'การฝึกล่าสุด - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'ดูประวัติการฝึกล่าสุด'
    },
    potential: {
      title: 'การประเมินศักยภาพ',
      pageTitle: 'รายงานการประเมินศักยภาพ',
      pageDescription: 'ดูรายงานการประเมินศักยภาพของคุณ',
      loadingPdfViewer: 'กำลังโหลดตัวดู PDF...',
      loadPdfFailed: 'โหลด PDF ไม่สำเร็จ',
      fetchReportFailed: 'โหลดรายงานไม่สำเร็จ'
    },
    topic: {
      title: 'รายละเอียดหัวข้อ',
      empty: 'ยังไม่มีสถานการณ์',
      cardDetailEmpty: 'ยังไม่มีรายละเอียด',
      cardDetailNotFound: 'ไม่พบเนื้อหาการ์ดที่ตรงกัน',
      loadCardDetailFailed: 'โหลดรายละเอียดการ์ดหัวข้อไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      loadFailed: 'โหลดรายการสถานการณ์ของหัวข้อไม่สำเร็จ โปรดลองอีกครั้งภายหลัง',
      pageTitle: 'รายละเอียดหัวข้อ - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'รายการสถานการณ์ของหัวข้อ'
    },
    info: {
      step1Title: 'กรอกข้อมูลงาน',
      step2Title: 'ยืนยันตำแหน่งและบริษัทเป้าหมาย',
      step2Tip: 'AI อาจรู้จำผิดพลาดได้ กรุณาตรวจสอบข้อมูลให้ถูกต้อง',
      step3Title: 'เลือกเรซูเม่',
      step3Tip: '<span class="text-[red]">*</span>ประกาศความเป็นส่วนตัว: ข้อมูลจะถูกเก็บเป็นความลับอย่างเคร่งครัดและใช้สำหรับการประเมินสัมภาษณ์ครั้งนี้เท่านั้น',
      step4Title: 'ตรวจสอบอุปกรณ์',
      next: 'ถัดไป',
      prev: 'กลับ',
      enterInterview: 'เข้าสู่ห้องสัมภาษณ์',
      autoRecognizing: 'กำลังรู้จำอัตโนมัติ',
      positionPlaceholder: 'กรอกชื่อตำแหน่ง',
      companyPlaceholder: 'กรอกชื่อบริษัท',
      noInfoDetected: 'ไม่พบข้อมูลที่ตรงกัน กรุณากรอกด้วยตนเอง',
      autoExtractFailed: 'การรู้จำอัตโนมัติล้มเหลว',
      positionTextareaPlaceholder: 'กรุณาวางหรือกรอกข้อมูลงาน รวมถึง: ชื่อตำแหน่ง ชื่อบริษัท และรายละเอียดงาน (JD)...',
      chooseHistoryResume: 'เลือกเรซูเม่เดิม',
      loadHistoryResumeFailed: 'โหลดประวัติเรซูเม่ไม่สำเร็จ',
      missingJobInfo: 'กรุณากรอกข้อมูลงาน',
      missingPositionAndCompany: 'กรุณากรอกทั้งชื่อตำแหน่งและชื่อบริษัท',
      missingPosition: 'กรุณากรอกชื่อตำแหน่ง',
      missingCompany: 'กรุณากรอกชื่อบริษัท',
      missingResume: 'กรุณาอัปโหลดเรซูเม่หรือเลือกเรซูเม่เดิม',
      missingMic: 'กรุณาเปิดไมโครโฟน'
    },
    result: {
      practiceResult: 'ผลการฝึก',
      waitingTitle: 'การสร้างรายงานอาจใช้เวลาสักครู่<br></br>กรุณารอด้วยความอดทน',
      processingConversation: 'กำลังประมวลผลบทสนทนา...',
      analyzingCommunication: 'กำลังวิเคราะห์ทักษะการสื่อสาร...',
      evaluatingEmotion: 'กำลังประเมินการจัดการอารมณ์...',
      generatingDetailed: 'กำลังสร้างรายงานแบบละเอียด...',
      overallScore: 'คะแนนรวม',
      practiceDuration: 'ระยะเวลาฝึก: {minutes} นาที',
      tabsLabel: 'มุมมองรายงาน',
      resultTab: 'ผลการประเมิน',
      reviewTab: 'ทบทวนบทสนทนา',
      abilityEvaluation: 'การประเมินความสามารถ',
      detailedEvaluation: 'การประเมินแบบละเอียด',
      objectiveCompletion: 'ความคืบหน้าของเป้าหมาย',
      doneWell: 'ทำได้ดี:',
      issues: 'ประเด็นที่พบ:',
      improve: 'ข้อเสนอแนะเพื่อปรับปรุง:',
      suggestions: 'คำแนะนำ',
      reviewLoadFailed: 'โหลดบันทึกการสนทนาไม่สำเร็จ',
      reviewLoading: 'กำลังโหลด โปรดรอสักครู่',
      noConversation: 'ยังไม่มีประวัติการสนทนา',
      missingThread: 'ไม่มี thread_id ไม่สามารถโหลดบทสนทนาได้',
      loadFailed: 'โหลดไม่สำเร็จ',
      roundContent: 'รอบที่ {round}: {content}',
      pageTitle: 'ผลการประเมิน - แพลตฟอร์มการเรียนรู้ AI Wangu',
      pageDescription: 'ผลการประเมินการฝึกและการประเมินความสามารถ'
    },
    resultComplete: {
      generatingReport: 'กำลังสร้างรายงานการประเมิน',
      interviewerGrading: 'ผู้สัมภาษณ์กำลังให้คะแนน...',
      interviewerEvaluation: 'ผู้สัมภาษณ์กำลังเขียนผลประเมินให้คุณ...',
      interviewerGuidance: 'ผู้สัมภาษณ์กำลังเขียนคำแนะนำให้คุณ...',
      securityBlocked: 'เนื้อหาผิดนโยบาย จึงไม่สามารถแสดงได้ชั่วคราว',
      interviewReport: 'รายงานการประเมินสัมภาษณ์',
      abilityTab: 'ความสามารถ',
      jobTab: 'ความเหมาะสมกับตำแหน่ง',
      answerTab: 'วิเคราะห์คำตอบ',
      adviceTab: 'คำแนะนำ',
      comprehensiveReport: 'รายงานการประเมินโดยรวม',
      abilityTable: 'ตารางประเมินความสามารถ',
      abilityDetail: 'รายละเอียดความสามารถ',
      jobAnalysis: 'วิเคราะห์ความเหมาะสมกับตำแหน่ง',
      answerAnalysis: 'วิเคราะห์การตอบคำถาม',
      generateFailed: 'สร้างผลลัพธ์ไม่สำเร็จ'
    }
  }
}

const resolveMessage = (locale: Locale, key: string): string => {
  const path = key.split('.')
  let current: MessageValue | undefined = messages[locale]

  for (const segment of path) {
    if (typeof current !== 'object' || current == null || !(segment in current)) {
      current = undefined
      break
    }
    current = current[segment]
  }

  if (typeof current === 'string') {
    return current
  }

  if (locale !== DEFAULT_LOCALE) {
    return resolveMessage(DEFAULT_LOCALE, key)
  }

  return key
}

export const normalizeLocale = (value?: string | null): Locale => {
  const lower = String(value || '').toLowerCase()

  if (lower.startsWith('th')) return 'th'
  if (lower.startsWith('en')) return 'en'
  if (lower.startsWith('zh')) return 'zh'

  return DEFAULT_LOCALE
}

const detectSystemLocale = (): Locale => {
  if (!import.meta.client) return DEFAULT_LOCALE

  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate)
    if (locale !== DEFAULT_LOCALE || String(candidate || '').toLowerCase().startsWith('zh')) {
      return locale
    }
  }

  return DEFAULT_LOCALE
}

const applyDocumentLang = (locale: Locale) => {
  if (!import.meta.client) return
  const htmlLangMap: Record<Locale, string> = {
    zh: 'zh-CN',
    en: 'en',
    th: 'th'
  }
  document.documentElement.lang = htmlLangMap[locale]
}

export function useAppI18n() {
  const locale = useState<Locale>('app-locale', () => DEFAULT_LOCALE)
  const initialized = useState<boolean>('app-locale-initialized', () => false)

  const localeOptions = computed(() => ([
    { value: 'zh' as const, label: resolveMessage(locale.value, 'common.chinese') },
    { value: 'en' as const, label: resolveMessage(locale.value, 'common.english') },
    { value: 'th' as const, label: resolveMessage(locale.value, 'common.thai') }
  ]))

  const setLocale = (nextLocale: Locale, persist = true) => {
    locale.value = nextLocale
    applyDocumentLang(nextLocale)

    if (persist && import.meta.client) {
      localStorage.setItem(LOCALE_STORAGE_KEY, nextLocale)
    }
  }

  const initLocale = () => {
    if (initialized.value || !import.meta.client) return

    const storedLocale = localStorage.getItem(LOCALE_STORAGE_KEY)
    setLocale(storedLocale ? normalizeLocale(storedLocale) : detectSystemLocale(), false)
    initialized.value = true
  }

  const t = (key: string, params?: Record<string, string | number>) => {
    let text = resolveMessage(locale.value, key)
    if (!params) return text

    for (const [name, value] of Object.entries(params)) {
      text = text.replaceAll(`{${name}}`, String(value))
    }
    return text
  }

  if (import.meta.client && !initialized.value) {
    initLocale()
  }

  return {
    locale,
    localeOptions,
    initLocale,
    setLocale,
    t
  }
}
